import type { LDFlagSet } from 'launchdarkly-react-client-sdk';

/**
 * Server-side rendering of this stack's LaunchDarkly Dev Server flag set into
 * the browser SDK's `options.bootstrap`.
 *
 * # Why the server has to fetch this, and the browser cannot
 *
 * admin-portal evaluates flags in the BROWSER
 * (`launchdarkly-react-client-sdk`), while stack-control hands the app a
 * cluster-internal Dev Server address -
 * `http://{namespace}-ld-dev-admin.{namespace}.svc.cluster.local:8766`. That is
 * exactly right for a server-side SDK (rotom points its Ruby client straight at
 * its own instance) and unusable from a browser three ways over: the name does
 * not resolve outside the cluster, plain HTTP is blocked as mixed content from
 * the HTTPS stack page, and the response would carry no CORS headers.
 *
 * So the Next server - which IS in the cluster - reads the flags once per page
 * load and hands them to the browser inside the document it is already sending.
 * The browser then needs no network path to the Dev Server at all: with
 * `options.bootstrap` set (plus the four suppressions
 * `providerConfig.ts` applies alongside it) the JS SDK makes no LaunchDarkly
 * request whatsoever. That is the whole point - there is no proxy route, no
 * path allowlist and no same-origin bridge to keep correct, because there is no
 * browser-to-Dev-Server traffic to bridge.
 *
 * # The accepted trade-off
 *
 * Flags are STATIC for the life of a page load. The Dev Server does implement
 * the SDK's SSE endpoint, and stack-control's `ld_flag_override` really does
 * push a live patch - but reaching it needs a browser-addressable stream, which
 * is the thing this design removes. On a Dev Server stack, flipping a flag is
 * therefore flip-then-reload. This was chosen deliberately over maintaining a
 * proxy for the sake of local development. (The server also remembers its last
 * read for a few seconds - see `DEV_SERVER_BOOTSTRAP_TTL_MS` in
 * `devServerBootstrapFetch.ts` - so a reload fired immediately after a flip can
 * need one more.)
 *
 * # Where this module is used from, and why the fetch is not in it
 *
 * Everything here is shared with client code (`_app.page.tsx`, `client.js`):
 * the element id and attribute name, the evalx-to-bootstrap mapping and
 * `readDevServerBootstrap`. So this module must not read
 * `LD_DEV_SERVER_URL`/`LD_DEV_SERVER_PROJECT_KEY`, and it does not.
 * `next.config.js` pushes its DefinePlugin outside the `isServer` guards, which
 * substitutes a `process.env.X` expression into WHICHEVER bundle compiles it -
 * and a stack runs `next dev`, which eliminates nothing. An env read in a
 * module the browser imports is an env value shipped to the browser, called or
 * not.
 *
 * The read, and the request it drives, live in `devServerBootstrapFetch.ts`,
 * which only `src/pages/_document.page.js` imports - and Next never bundles a
 * Document for the browser. Keeping the two apart is what makes "the address
 * and project key stay on the server" true by construction rather than by
 * convention.
 */

/**
 * The element the server-rendered document emits and the browser reads back,
 * and the attribute on it that holds the JSON.
 *
 * Exported because two places have to agree on them: `_document` and
 * `readDevServerBootstrap` below.
 *
 * The payload is an attribute value, not inline script, so React escapes it as
 * data and no flag key or value can be interpreted as markup or code.
 */
export const LD_DEV_SERVER_BOOTSTRAP_ELEMENT_ID = 'ld-dev-server-bootstrap';
export const LD_DEV_SERVER_BOOTSTRAP_ATTRIBUTE = 'data-bootstrap';

/**
 * What crosses from the server render to the browser.
 *
 * The project key travels WITH the flags rather than being read from the
 * environment a second time in the browser: `LD_DEV_SERVER_URL` and
 * `LD_DEV_SERVER_PROJECT_KEY` are referenced only by `devServerBootstrapFetch.ts`,
 * which is in the server bundle alone (see the module header). The browser's
 * whole notion of "this stack has a Dev Server" is "a bootstrap arrived", which
 * is also the only condition under which pointing the SDK at a project key is
 * safe - see `providerConfig.ts`.
 */
export interface LDDevServerBootstrap {
  /**
   * The LaunchDarkly PROJECT key, which the Dev Server reads as an unvalidated
   * credential and which the browser SDK is handed as its `clientSideID`.
   */
  projectKey: string;
  /**
   * The flag set in the JS SDK's `options.bootstrap` shape - see
   * `devServerBootstrapFlags`.
   */
  flags: LDFlagSet;
}

/** One flag as the Dev Server's `/sdk/evalx` response describes it. */
export interface EvalxFlagDetail {
  value?: unknown;
  variation?: number;
  version?: number;
  trackEvents?: boolean;
  trackReason?: boolean;
  debugEventsUntilDate?: number;
  reason?: unknown;
}

/**
 * The `/sdk/evalx` response, remapped into the SDK's bootstrap shape.
 *
 * These are two different formats and the SDK accepts only the second. Read
 * `readFlagsFromBootstrap` in `launchdarkly-js-sdk-common/src/index.js`: it
 * walks the TOP-LEVEL keys taking each one's VALUE, and looks up that key's
 * metadata under the reserved `$flagsState` key. `/sdk/evalx` instead nests
 * both together as `{[key]: {value, variation, version, ...}}`. Handing the
 * evalx response over unmapped would make every flag's value an OBJECT, so
 * every `useFeatureFlag` boolean check would be truthy.
 *
 * `$flagsState` is supplied rather than omitted. Omitting it is tolerated - the
 * SDK warns `bootstrapOldFormat` and stamps `version: 0` on everything - but
 * the metadata is right there in the response, and a console warning on every
 * page load of a dev stack is exactly the kind of noise a dev stack exists to
 * avoid.
 */
export function devServerBootstrapFlags(evalx: Record<string, EvalxFlagDetail>): LDFlagSet {
  const flagsState: Record<string, EvalxFlagDetail> = {};
  // `$valid: true` is asserted, not copied: the SDK only ever READS this to
  // decide whether to warn `bootstrapInvalid`, and we are here because a
  // successful evalx response was parsed.
  const flags: LDFlagSet = { $flagsState: flagsState, $valid: true };

  for (const [key, detail] of Object.entries(evalx)) {
    // A Dev Server response containing a literal `$flagsState` would otherwise
    // overwrite the metadata map we are building. No real flag key can start
    // with `$` (the SDK reserves the prefix), so skipping is not a lossy guard.
    if (key.startsWith('$') || typeof detail !== 'object' || detail === null) continue;

    const { value, ...metadata } = detail;

    flags[key] = value;
    // `version` is defaulted rather than left out because the SDK only supplies
    // its own `version: 0` fallback for flags with NO metadata entry at all -
    // and every flag here has one.
    flagsState[key] = { ...metadata, version: metadata.version ?? 0 };
  }

  return flags;
}

let cached: { raw: string; bootstrap: LDDevServerBootstrap } | undefined;

/**
 * The bootstrap the server-rendered document left in the DOM, if any.
 *
 * The browser's ONLY signal that this stack has a Dev Server. Two callers need
 * it for two reasons: `_app.page.tsx` to configure the SDK after hydration and
 * on every client-side navigation that follows (`_document` runs once per full
 * page load, so the element is what carries the flags through the SPA), and
 * `client.js` to suppress `identify()`, which always goes to the network.
 *
 * The JSON is parsed once and the object reused for as long as the attribute is
 * unchanged, so every caller sees the same reference and `_app`'s `useMemo`
 * does not rebuild the SDK config on each render.
 *
 * Returns `undefined` during SSR. That is not a hydration hazard: `_app`
 * prefers the prop `_document` injected into the server render, and reaches for
 * this only in the browser, so both renders see the same flags.
 */
export function readDevServerBootstrap(): LDDevServerBootstrap | undefined {
  if (typeof document === 'undefined') return undefined;

  const raw = document
    .getElementById(LD_DEV_SERVER_BOOTSTRAP_ELEMENT_ID)
    ?.getAttribute(LD_DEV_SERVER_BOOTSTRAP_ATTRIBUTE);

  if (!raw) return undefined;
  if (cached?.raw !== raw) {
    // Throws rather than returning `undefined`: `undefined` reads as "no Dev
    // Server" and would send the browser to hosted LaunchDarkly, which the
    // stack must stay off.
    try {
      cached = { raw, bootstrap: JSON.parse(raw) };
    } catch {
      throw new Error(
        `#${LD_DEV_SERVER_BOOTSTRAP_ELEMENT_ID} holds invalid JSON in ${LD_DEV_SERVER_BOOTSTRAP_ATTRIBUTE}`
      );
    }
  }

  return cached.bootstrap;
}
